/**
 * Mudar o plano de uma conta (docs/spec/planos.md). O admin escolhe o plano, o prazo e a nota interna;
 * quem diz o que o plano dá é o servidor. Antes de confirmar, o diálogo avisa quando a conta usa mais
 * espaço do que o plano novo dá (nada é apagado).
 *
 * `askPlan(row)` devolve uma promessa, como as perguntas de `ui/prompt.tsx`, e o <AdminPlanHost/> (na
 * área de administração) desenha o diálogo.
 */
import { useState } from 'react';
import { create } from 'zustand';
import { api } from '../api.js';
import { formatBytes } from '../bytes.js';
import { useT } from '../i18n/index.js';
import { DEFAULT_PLAN, PLAN_IDS, PLAN_LIMITS, type AdminUserRow, type PlanId } from '../shared.js';
import { dateInputValue, endOfDay, planSummary, suggestedExpiry } from '../plans/planModel.js';
import { usePlanGrid, usePlanName } from '../plans/usePlanName.js';
import { describeError, useApp } from '../store.js';
import { Button, CheckBox, ComboBox, Dialog, TextEdit } from '../ui/controls.js';
import { toast } from '../ui/Toaster.js';

/** A conta do diálogo: quem é, quanto usa e o plano que vale agora. */
export type PlanDialogRow = Pick<AdminUserRow, 'user' | 'usedBytes' | 'plan'>;

type PlanAsk = { row: PlanDialogRow; resolve: (changed: boolean) => void };

const usePlanAsk = create<{ ask: PlanAsk | null }>()(() => ({ ask: null }));

/** Abre o diálogo; a promessa diz se o plano mudou (cancelar ou um erro do servidor: não). */
export function askPlan(row: PlanDialogRow): Promise<boolean> {
  return new Promise((resolve) => usePlanAsk.setState({ ask: { row, resolve } }));
}

export function AdminPlanHost() {
  const ask = usePlanAsk((s) => s.ask);
  if (!ask) return null;
  const done = (changed: boolean) => {
    usePlanAsk.setState({ ask: null });
    ask.resolve(changed);
  };
  return <AdminPlanDialog key={ask.row.user.id} row={ask.row} onDone={done} />;
}

export function AdminPlanDialog({ row, onDone }: { row: PlanDialogRow; onDone: (changed: boolean) => void }) {
  const t = useT();
  const name = row.user.displayName;
  const planName = usePlanName();
  const grid = usePlanGrid();
  // o "agora" de quando o diálogo abriu: o desenho não chama o relógio a cada vez
  const [openedAt] = useState(() => Date.now());
  const [plan, setPlan] = useState<PlanId>(row.plan.id);
  const [noExpiry, setNoExpiry] = useState(row.plan.id === DEFAULT_PLAN || row.plan.expiresAt === null);
  const [date, setDate] = useState(() => dateInputValue(suggestedExpiry(row.plan.expiresAt, openedAt)));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const free = plan === DEFAULT_PLAN;
  const expiresAt = free || noExpiry ? null : endOfDay(date);
  const invalidDate = !free && !noExpiry && (expiresAt === null || expiresAt <= openedAt);
  // o mesmo plano com o mesmo prazo não muda nada (o servidor responderia 409)
  const unchanged = plan === row.plan.id && expiresAt === row.plan.expiresAt;
  const space = grid[plan].storageBytes;
  const overUse = row.usedBytes > space;

  const submit = async () => {
    const token = useApp.getState().token;
    if (!token || busy) return;
    setBusy(true);
    try {
      await api.adminSetPlan(token, row.user.id, { plan, expiresAt, note: note.trim() });
      toast(t('admin.plan.done', { name, plan: planName(plan) }), 'success');
      onDone(true);
    } catch (error) {
      toast(describeError(error), 'error');
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t('admin.plan.title', { name })}
      onClose={() => onDone(false)}
      actions={
        <>
          <Button variant="secondary" onClick={() => onDone(false)}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" busy={busy} disabled={invalidDate || unchanged} onClick={() => void submit()}>
            {t('admin.action.plan')}
          </Button>
        </>
      }
    >
      <div className="rd-planDialog">
        <label className="rd-planDialog__field">
          <span>{t('admin.plan.plan')}</span>
          <ComboBox
            value={plan}
            onChange={setPlan}
            aria-label={t('admin.plan.plan')}
            options={PLAN_IDS.map((id) => ({ value: id, label: `${planName(id)} · ${planSummary(t, grid[id])}` }))}
          />
        </label>
        {free ? (
          <p className="rd-planDialog__hint">{t('admin.plan.freeNoExpiry')}</p>
        ) : (
          <>
            <CheckBox checked={noExpiry} onChange={setNoExpiry}>
              {t('admin.plan.noExpiry')}
            </CheckBox>
            {!noExpiry ? (
              <label className="rd-planDialog__field">
                <span>{t('admin.plan.validUntil')}</span>
                <input
                  type="date"
                  className={`rd-planDialog__date${invalidDate ? ' is-invalid' : ''}`}
                  value={date}
                  min={dateInputValue(openedAt)}
                  onChange={(e) => setDate(e.target.value)}
                  aria-invalid={invalidDate}
                />
              </label>
            ) : null}
          </>
        )}
        <label className="rd-planDialog__field">
          <span>{t('admin.plan.note')}</span>
          <TextEdit look="filled" value={note} onChange={setNote} maxLength={PLAN_LIMITS.note} aria-label={t('admin.plan.note')} />
        </label>
        {unchanged ? (
          <p className="rd-planDialog__hint" role="status">
            {t('admin.plan.same')}
          </p>
        ) : null}
        {overUse ? (
          <p className="rd-planDialog__warning" role="alert">
            {t('admin.plan.overUse', { used: formatBytes(row.usedBytes), plan: planName(plan), space: formatBytes(space) })}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
